import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fAPIDate } from 'src/utils/format-time';

import {
  IIssuedChequeItem,
  IIssuedChequeListItem,
  IIssuedChequePayment,
} from 'src/types/issued-cheque';

import axios from '../../utils/axios';

type Status = 'idle' | 'loading' | 'success' | 'failure';

interface IInitialState {
  status: Status;
  error: string | undefined;
  cheques: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IIssuedChequeListItem[];
  };
  total: { status: Status; data: { totalAmount: number; count: number } };
  item: IIssuedChequeItem | null;
  itemStatus: Status;
  itemError: string | undefined;
  payments: { status: Status; items: IIssuedChequePayment[] };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  cheques: { count: 0, page: 1, pageSize: 25, pageCount: 0, items: [] },
  total: { status: 'idle', data: { totalAmount: 0, count: 0 } },
  item: null,
  itemStatus: 'idle',
  itemError: undefined,
  payments: { status: 'idle', items: [] },
};

export type IIssuedChequeQuery = {
  generalSearch?: string | null;
  status?: string | null;
  type?: string | null;
  bank?: string | null;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
};

// Empty filters are left off so the server doesn't see blank enum values.
const toParams = (query: IIssuedChequeQuery) => {
  const params: Record<string, string> = {};

  if (query.generalSearch) params.generalSearch = query.generalSearch;
  if (query.status) params.status = query.status;
  if (query.type) params.type = query.type;
  if (query.bank) params.bank = query.bank;
  if (query.startDate) params.startDate = fAPIDate(query.startDate);
  if (query.endDate) params.endDate = fAPIDate(query.endDate);

  return params;
};

export const getIssuedCheques = createAsyncThunk(
  'issuedCheque/getList',
  async (
    params: IIssuedChequeQuery & { page: number; limit: number; sortBy: string; sort: string }
  ) => {
    const { page, limit, sortBy, sort, ...query } = params;

    const response = await axios.get('issued-cheque', {
      params: { ...toParams(query), page: page + 1, limit, sortBy, sort },
    });

    return response.data.data;
  }
);

export const getIssuedChequeTotal = createAsyncThunk(
  'issuedCheque/getTotal',
  async (query: IIssuedChequeQuery) => {
    const response = await axios.get('issued-cheque/total', { params: toParams(query) });

    return response.data.data;
  }
);

export const getIssuedChequeById = createAsyncThunk(
  'issuedCheque/getById',
  async (params: { id: string }) => {
    const response = await axios.get(`issued-cheque/${params.id}`);

    return response.data.data;
  }
);

// The calendar keeps its own slim rows, so this isn't stored in Redux.
export const fetchIssuedChequesForCalendar = async (
  query: IIssuedChequeQuery
): Promise<IIssuedChequeListItem[]> => {
  const response = await axios.get('issued-cheque/calendar', { params: toParams(query) });

  return response.data.data;
};

export type TIssuedChequeInput = {
  bank: string;
  chequeNumber: string;
  chequeDate: Date;
  amountInCents: number;
  type: string;
  payee: string;
  note?: string | null;
};

export const createIssuedCheque = createAsyncThunk(
  'issuedCheque/create',
  async (cheque: TIssuedChequeInput) => {
    const response = await axios.post('issued-cheque', cheque);

    return response.data.data;
  }
);

export const updateIssuedCheque = createAsyncThunk(
  'issuedCheque/update',
  async (params: { id: string; cheque: TIssuedChequeInput }) => {
    const response = await axios.patch(`issued-cheque/${params.id}`, params.cheque);

    return response.data.data;
  }
);

export const updateIssuedChequeStatus = createAsyncThunk(
  'issuedCheque/updateStatus',
  async (params: { id: string; status: string; date: Date; note?: string }) => {
    const { id, ...body } = params;
    const response = await axios.patch(`issued-cheque/${id}/status`, body);

    return response.data.data;
  }
);

export const getIssuedChequePayments = createAsyncThunk(
  'issuedCheque/getPayments',
  async (params: { id: string }) => {
    const response = await axios.get(`issued-cheque/${params.id}/payments`);

    return response.data.data;
  }
);

export const createIssuedChequePayment = createAsyncThunk(
  'issuedCheque/createPayment',
  async (params: { id: string; paymentDate: Date; amountInCents: number; note?: string }) => {
    const { id, ...body } = params;
    const response = await axios.post(`issued-cheque/${id}/payments`, body);

    return response.data.data;
  }
);

export const deleteIssuedChequePayment = createAsyncThunk(
  'issuedCheque/deletePayment',
  async (params: { id: string; paymentId: string }) => {
    const response = await axios.delete(`issued-cheque/${params.id}/payments/${params.paymentId}`);

    return response.data.data;
  }
);

const issuedChequeSlice = createSlice({
  name: 'issuedCheque',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getIssuedCheques.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getIssuedCheques.fulfilled, (state, action) => {
        state.status = 'success';
        state.cheques = action.payload;
      })
      .addCase(getIssuedCheques.rejected, (state, action) => {
        state.status = 'failure';
        state.error = action.error.message;
      })

      .addCase(getIssuedChequeTotal.pending, (state) => {
        state.total.status = 'loading';
      })
      .addCase(getIssuedChequeTotal.fulfilled, (state, action) => {
        state.total.status = 'success';
        state.total.data = action.payload;
      })
      .addCase(getIssuedChequeTotal.rejected, (state) => {
        state.total.status = 'failure';
      })

      .addCase(getIssuedChequeById.pending, (state) => {
        state.itemStatus = 'loading';
      })
      .addCase(getIssuedChequeById.fulfilled, (state, action) => {
        state.itemStatus = 'success';
        state.item = action.payload;
      })
      .addCase(getIssuedChequeById.rejected, (state, action) => {
        state.itemStatus = 'failure';
        state.itemError = action.error.message;
      })

      .addCase(getIssuedChequePayments.pending, (state) => {
        state.payments.status = 'loading';
      })
      .addCase(getIssuedChequePayments.fulfilled, (state, action) => {
        state.payments.status = 'success';
        state.payments.items = action.payload;
      })
      .addCase(getIssuedChequePayments.rejected, (state) => {
        state.payments.status = 'failure';
      });
  },
});

export default issuedChequeSlice.reducer;
